import { type CalculatorDef, type CalcResult, checkFinite, err, num, ok } from './types';

/** Density of pure ethanol at 20 °C, kg/l. */
export const ETHANOL_DENSITY = 0.7893;

export type AbsoluteAlcoholResult = {
  /** Litres of pure (100%) ethanol contained in the spirit. */
  absoluteAlcohol: number;
  /** Mass of that ethanol, kg. */
  ethanolMass: number;
  /** Equivalent litres if the whole lot were brought to 50 %ABV. */
  equivalentAt50: number;
}

/**
 * Litres of pure ethanol in a given quantity of spirit.
 *
 * Trivial arithmetic — `volume x abv / 100` — but it is the number duties, allowances and
 * distilling records are all denominated in, so it earns its own calculator rather than
 * making people do it in their head.
 *
 * Unlike the other calculators here this one involves no mixing, so the ideal-mixing
 * caveat does not apply: it is exact given an exact input strength. The uncertainty is
 * entirely in how well you measured the ABV.
 *
 * Reporting thresholds and tax-free allowances change and vary by country. This tells you
 * a quantity; it does not tell you your obligations.
 */
export function absoluteAlcohol(volume: number, abv: number): CalcResult<AbsoluteAlcoholResult> {
  const invalid = checkFinite({ volume, abv });
  if (invalid) return { ok: false, error: invalid };

  if (volume < 0) return err('error.volumeNonNegative', undefined, 'volume');
  if (abv < 0 || abv > 100) return err('error.abvRange', undefined, 'abv');

  const pure = (volume * abv) / 100;

  return ok({
    absoluteAlcohol: pure,
    ethanolMass: pure * ETHANOL_DENSITY,
    equivalentAt50: pure * 2,
  });
}

export const absoluteAlcoholCalculator: CalculatorDef = {
  id: 'absoluteAlcohol',
  notesKey: 'notes.absoluteAlcohol',
  fields: [
    { name: 'volume', unit: 'litre', min: 0, max: 10000, step: 0.1, defaultValue: 20 },
    { name: 'abv', unit: 'percent', min: 0, max: 100, step: 0.1, defaultValue: 52 },
  ],
  outputs: [
    { name: 'absoluteAlcohol', unit: 'litre', precision: 2, primary: true },
    { name: 'equivalentAt50', unit: 'litre', precision: 2 },
    { name: 'ethanolMass', unit: 'kilogram', precision: 2 },
  ],
  compute: (i) => absoluteAlcohol(num(i, 'volume'), num(i, 'abv')),
};
